import { inject, Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { DatabaseViewSchema } from '../../../../api/app-types/database-view.app';
import { REPRESENTATION_MODE } from '../../../../core/services/representation-mode.service';
import { SessionStorageService } from '../../../../shared/storage/session-storage.service';
import { SessionManagerService } from '../../../../shared/storage/session-manager.service';
import { DATABASE_OPTIONS_TTL_MS, DATABASE_VIEW_STORAGE_PREFIX } from '../../database-view-storage.constants';
import {
	DatabaseFilterOption,
	DatabaseOptionsEntry,
	DatabaseSchemaEntry,
	DatabaseTabId,
	isDatabaseTabId,
} from '../models/database-view-storage.types';

const SESSION_CACHE_PREFIX = `${DATABASE_VIEW_STORAGE_PREFIX}.cache.`;
const SESSION_STATE_PREFIX = `${DATABASE_VIEW_STORAGE_PREFIX}.state.`;
const OWNER_KEY = `${DATABASE_VIEW_STORAGE_PREFIX}.owner`;
const LAST_TAB_NAME = 'lastTab';

/**
 * Owns database view state for the active user, tenant and representation.
 * List state and last tab live in browser-tab sessionStorage; schemas and dropdown options live there too, with TTL for options.
 * Pending loads are shared across list recreation and only commit while their session generation and context still match.
 */
@Injectable({ providedIn: 'root' })
export class DatabaseViewStorage {
	private readonly session = inject(SessionManagerService);
	private readonly sessionStorage = inject(SessionStorageService);
	private readonly auth = inject(MsalService);
	private readonly representation = inject(REPRESENTATION_MODE);

	private readonly pending = new Map<string, Promise<DatabaseFilterOption[] | null>>();
	private readonly refreshedTabs = new Set<DatabaseTabId>();
	private readonly pageLoadIsReload = isPageReload();
	private suspended = false;

	public constructor() {
		this.session.onInvalidate((event) => {
			this.suspended = true;
			this.pending.clear();
			this.refreshedTabs.clear();
			if (event.reason === 'recovery') {
				this.sessionStorage.removeByPrefix(SESSION_CACHE_PREFIX);
			} else if (event.reason === 'logout' && event.localAccountId) {
				this.clearAccount(event.localAccountId);
			} else {
				this.sessionStorage.removeByPrefix(`${DATABASE_VIEW_STORAGE_PREFIX}.`);
			}
		});
		this.session.onEstablish(() => {
			this.suspended = false;
		});
	}

	public getLastTab(): DatabaseTabId | undefined {
		const context = this.currentContext();
		if (!context) return undefined;
		const value = this.sessionStorage.read<unknown>(this.stateKey(context, LAST_TAB_NAME));
		return isDatabaseTabId(value) ? value : undefined;
	}

	public setLastTab(tab: DatabaseTabId): void {
		const context = this.currentContext();
		if (!context) return;
		this.sessionStorage.write(this.stateKey(context, LAST_TAB_NAME), tab);
	}

	/** Reads the list state for a tab in the current context. The parser turns unknown stored data into a typed state or undefined. */
	public readListState<T>(tab: DatabaseTabId, parse: (raw: unknown) => T | undefined): T | undefined {
		const context = this.currentContext();
		if (!context) return undefined;
		return parse(this.sessionStorage.read<unknown>(this.stateKey(context, tab)));
	}

	public writeListState<T>(tab: DatabaseTabId, state: T): void {
		const context = this.currentContext();
		if (!context) return;
		this.sessionStorage.write(this.stateKey(context, tab), state);
	}

	/** Schema is stable for the whole session, so it has no TTL. A different view name is treated as a miss. */
	public readSchema<TSchema>(
		tab: DatabaseTabId,
		view: string,
		parse: (raw: unknown) => TSchema | undefined,
	): TSchema | undefined {
		const context = this.currentContext();
		if (!context) return undefined;
		const entry = this.sessionStorage.read<DatabaseSchemaEntry<unknown>>(this.cacheKey(context, `schema.${tab}`));
		if (!entry || entry.view !== view) return undefined;
		return parse(entry.schema);
	}

	public writeSchema<TSchema>(tab: DatabaseTabId, view: string, schema: TSchema): void {
		const context = this.currentContext();
		if (!context) return;
		const entry: DatabaseSchemaEntry<TSchema> = { view, schema };
		this.sessionStorage.write(this.cacheKey(context, `schema.${tab}`), entry);
	}

	/** Returns the cached schema for the view, or loads it and caches it unless the load went stale while in flight. */
	public async resolveSchema(
		tab: DatabaseTabId,
		view: string,
		load: () => Promise<DatabaseViewSchema>,
		bypassCache = false,
	): Promise<DatabaseViewSchema> {
		const context = this.currentContext();
		if (context && !bypassCache) {
			const cached = this.readSchema(tab, view, parseDatabaseViewSchema);
			if (cached) return cached;
		}
		const generation = this.session.generation;
		const schema = await load();
		if (context && generation === this.session.generation && context === this.currentContext()) {
			this.writeSchema(tab, view, schema);
		}
		return schema;
	}

	/**
	 * Resolves dropdown sources for a tab. Fresh cache entries are reused, the rest are loaded in one batch.
	 * A source that returns null (failure) is never cached and stays unavailable for this call.
	 */
	public async resolveSourceOptions<TKey extends string>(
		tab: DatabaseTabId,
		keys: readonly TKey[],
		load: (missing: TKey[]) => Promise<Record<TKey, DatabaseFilterOption[] | null>>,
		bypassCache = false,
	): Promise<Record<TKey, DatabaseFilterOption[] | null>> {
		const context = this.currentContext();
		const generation = this.session.generation;
		const cachedValues = new Map<TKey, DatabaseFilterOption[]>();
		const awaited = new Map<TKey, Promise<DatabaseFilterOption[] | null>>();
		const needsRequest: TKey[] = [];

		for (const key of keys) {
			const cached = !bypassCache && context ? this.readFreshOptions(context, tab, key) : undefined;
			if (cached) {
				cachedValues.set(key, cached);
				continue;
			}
			const existing = this.pending.get(this.pendingId(context, tab, key));
			if (existing) {
				awaited.set(key, existing);
			} else {
				needsRequest.push(key);
			}
		}

		if (needsRequest.length > 0) {
			const batch = load(needsRequest).catch(
				() => Object.fromEntries(needsRequest.map((key) => [key, null])) as Record<TKey, DatabaseFilterOption[] | null>,
			);
			for (const key of needsRequest) {
				const id = this.pendingId(context, tab, key);
				const promise = batch.then((values) => {
					const options = values[key] ?? null;
					if (options && context && generation === this.session.generation && context === this.currentContext()) {
						this.writeOptions(context, tab, key, options);
					}
					return options;
				});
				this.pending.set(id, promise);
				awaited.set(key, promise);
				void promise.finally(() => {
					if (this.pending.get(id) === promise) this.pending.delete(id);
				});
			}
		}

		const resolved = {} as Record<TKey, DatabaseFilterOption[] | null>;
		await Promise.all(
			keys.map(async (key) => {
				resolved[key] = cachedValues.get(key) ?? (await awaited.get(key)) ?? null;
			}),
		);
		return resolved;
	}

	/** True only on a browser reload of the page, and only until the tab has completed one refreshed load. */
	public shouldBypassCache(tab: DatabaseTabId): boolean {
		return this.pageLoadIsReload && !this.refreshedTabs.has(tab);
	}

	public markTabRefreshed(tab: DatabaseTabId): void {
		this.refreshedTabs.add(tab);
	}

	private readFreshOptions(context: string, tab: DatabaseTabId, key: string): DatabaseFilterOption[] | undefined {
		const entry = this.sessionStorage.read<DatabaseOptionsEntry>(this.cacheKey(context, `options.${tab}.${key}`));
		if (!isOptionsEntry(entry)) return undefined;
		const age = Date.now() - entry.fetchedAt;
		return age >= 0 && age < DATABASE_OPTIONS_TTL_MS ? entry.options : undefined;
	}

	private writeOptions(context: string, tab: DatabaseTabId, key: string, options: DatabaseFilterOption[]): void {
		const entry: DatabaseOptionsEntry = { fetchedAt: Date.now(), options };
		this.sessionStorage.write(this.cacheKey(context, `options.${tab}.${key}`), entry);
	}

	private currentContext(): string | undefined {
		if (this.suspended) return undefined;
		const account = this.auth.instance.getActiveAccount();
		if (!account?.localAccountId) return undefined;
		const tenantId = account.tenantId ?? '';
		this.claimOwner(ownerPrefix(account.localAccountId, tenantId));
		return JSON.stringify([account.localAccountId, tenantId, this.representation.mode().representedUserId ?? '']);
	}

	/** Drops list state and cache of the previous account or tenant when the browser tab switches to another one. */
	private claimOwner(owner: string): void {
		const current = this.sessionStorage.read<unknown>(OWNER_KEY);
		if (current === owner) return;
		if (typeof current === 'string') {
			this.removeScope(current);
		}
		this.sessionStorage.write(OWNER_KEY, owner);
	}

	private clearAccount(localAccountId: string): void {
		const prefix = accountPrefix(localAccountId);
		this.removeScope(prefix);
		const owner = this.sessionStorage.read<unknown>(OWNER_KEY);
		if (typeof owner === 'string' && owner.startsWith(prefix)) {
			this.sessionStorage.remove(OWNER_KEY);
		}
	}

	private removeScope(rawPrefix: string): void {
		const encoded = encodeURIComponent(rawPrefix);
		this.sessionStorage.removeByPrefix(`${SESSION_STATE_PREFIX}${encoded}`);
		this.sessionStorage.removeByPrefix(`${SESSION_CACHE_PREFIX}${encoded}`);
	}

	private stateKey(context: string, name: string): string {
		return `${SESSION_STATE_PREFIX}${encodeURIComponent(context)}.${name}`;
	}

	private cacheKey(context: string, name: string): string {
		return `${SESSION_CACHE_PREFIX}${encodeURIComponent(context)}.${name}`;
	}

	private pendingId(context: string | undefined, tab: DatabaseTabId, key: string): string {
		return `${context ?? ''}|${tab}|${key}`;
	}
}

const accountPrefix = (localAccountId: string): string => `[${JSON.stringify(localAccountId)},`;

// Prefix of the serialized context [localAccountId, tenantId, representedUserId] for one account and tenant.
const ownerPrefix = (localAccountId: string, tenantId: string): string =>
	`${accountPrefix(localAccountId)}${JSON.stringify(tenantId)},`;

const parseDatabaseViewSchema = (value: unknown): DatabaseViewSchema | undefined => {
	if (typeof value !== 'object' || value === null) return undefined;
	const schema = value as Partial<DatabaseViewSchema>;
	return typeof schema.view === 'string' && Array.isArray(schema.columns) ? (schema as DatabaseViewSchema) : undefined;
};

// Entries written before options carried a label are treated as a cache miss and reloaded.
const isFilterOption = (value: unknown): value is DatabaseFilterOption => {
	if (typeof value !== 'object' || value === null) return false;
	const option = value as Partial<DatabaseFilterOption>;
	return typeof option.value === 'string' && typeof option.label === 'string';
};

const isOptionsEntry = (value: unknown): value is DatabaseOptionsEntry => {
	if (typeof value !== 'object' || value === null) return false;
	const entry = value as Partial<DatabaseOptionsEntry>;
	return typeof entry.fetchedAt === 'number' && Array.isArray(entry.options) && entry.options.every(isFilterOption);
};

const isPageReload = (): boolean => {
	if (typeof performance === 'undefined' || typeof performance.getEntriesByType !== 'function') return false;
	return performance
		.getEntriesByType('navigation')
		.some((entry) => (entry as PerformanceNavigationTiming).type === 'reload');
};
